import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {DLPageTypeEnum} from '../../../enums/DataLayerEnums';
import {RoutesAddonsEnums, RoutesEnums, RoutesNamesEnum} from '../../../enums/RoutesEnums';
import {UrlTermsSymbolsEnum} from '../../../enums/SeoEnums';
import {AppConfigService} from '../../../services/app-config.service';
import {PageViewsGa4ResolverService} from '../../../services/page-views-ga4-resolver.service';
import {SeoMetaService} from '../../../services/seo-meta.service';
import {UrlBuilderService} from '../../../services/url-builder.service';
import {DataLayerParamsPageViewGA4} from '../../../types/DataLayerParamsPageViewGA4';
import {MetaTag} from '../../../types/MetaTag';
import {SearchWidgetDataItem} from '../../../types/SearchWidgetDataItem';

@Injectable({
  providedIn: 'root',
})
export class RelatedService {
  constructor(
    private urlBuilder: UrlBuilderService,
    private metaService: SeoMetaService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private appConfigService: AppConfigService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {}

  public trackPageView(type: RoutesAddonsEnums, typeName: string, item: SearchWidgetDataItem): void {
    const customGa4Params: DataLayerParamsPageViewGA4 = {
      pageType: DLPageTypeEnum.SERP_RELATED_PAGE,
      pageTitle: this.getPageTitle(typeName, item),
      mainCareProviderTypeId: type === RoutesAddonsEnums.RELATED_CAREPROVIDER && item ? item.id : null,
      inferredCategoryTypeId: type === RoutesAddonsEnums.RELATED_CAREPROVIDER && item ? item.id : null,
      careServiceTypeId: type === RoutesAddonsEnums.RELATED_CAREPROVIDER && item ? item.id : null,
      symptomId: type === RoutesAddonsEnums.RELATED_CAREPROVIDER && item ? item.id : null,
    };
    this.pageViewGa4Resolver.trackPageView(customGa4Params);
  }

  public updateMetaTags(
    type: RoutesAddonsEnums,
    typeName: string,
    item: SearchWidgetDataItem,
    suffix: UrlTermsSymbolsEnum,
  ): void {
    const metaTags: MetaTag[] = [];
    const pageTitle: string = this.getPageTitle(typeName, item);
    const description: string = this.createMetaDescription(typeName, item);
    const pageUrl: string = this.getPageUrl(type, item, suffix);

    // Basic meta tags
    metaTags.push({
      value: pageTitle,
      type: 'title',
      field: 'title',
    });

    metaTags.push({
      value: description,
      type: 'name',
      field: 'description',
    });

    // Open Graph tags
    metaTags.push({
      value: pageTitle,
      type: 'property',
      field: 'og:title',
    });

    metaTags.push({
      value: 'website',
      type: 'property',
      field: 'og:type',
    });

    metaTags.push({
      value: pageUrl,
      type: 'property',
      field: 'og:url',
    });

    metaTags.push({
      value: description,
      type: 'property',
      field: 'og:description',
    });

    metaTags.push({
      value: this.appConfigService.appConfig.siteName,
      type: 'property',
      field: 'og:site_name',
    });

    // Twitter Card tags for better social sharing
    metaTags.push({
      value: 'summary_large_image',
      type: 'name',
      field: 'twitter:card',
    });

    metaTags.push({
      value: pageTitle,
      type: 'name',
      field: 'twitter:title',
    });

    metaTags.push({
      value: description,
      type: 'name',
      field: 'twitter:description',
    });

    // Canonical URL for SEO
    metaTags.push({
      value: pageUrl,
      type: 'link',
      field: 'canonical',
    });

    this.metaService.updateMetaTags(metaTags);
    this.addStructuredData(typeName, item, pageUrl);
  }

  private getPageTitle(typeName: string, item: SearchWidgetDataItem): string {
    if (typeName === RoutesNamesEnum.RELATED_CAREPROVIDER && !item) {
      return `Hitta Vårdcentral, Tandläkare, Hudläkare, Psykolog, Naprapat och mer – Jämför & Boka`;
    }
    return `Relaterade sökningar på ${item ? item.name : typeName}`;
  }

  private getPageUrl(type: RoutesAddonsEnums, item: SearchWidgetDataItem, suffix: UrlTermsSymbolsEnum): string {
    const routerLink: string[] = ['/', RoutesEnums.RELATED, type];
    if (item) {
      routerLink.push(`${item.urlPath}-${suffix}`);
    }
    return this.urlBuilder.CreateStaticLink(routerLink);
  }

  private createMetaDescription(typeName: string, item: SearchWidgetDataItem): string {
    if (typeName === RoutesNamesEnum.RELATED_CAREPROVIDER && !item) {
      return (
        'Jämför och boka de bästa legitimerade vårdgivarna såsom vårdcentraler, tandläkare, ' +
        'psykologer, naprapater, kiropraktorer, hudläkare och ögonläkare. Se omdömen och betyg, ' +
        'lista dig på en vårdcentral nära dig.'
      );
    }

    const name: string = item ? item.name : typeName;
    return `Utforska relaterade sökningar för ${name} och hitta den bästa vården för dina behov. Få mer information och rekommendationer hos oss.`;
  }

  private addStructuredData(typeName: string, item: SearchWidgetDataItem, pageUrl: string): void {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: this.getPageTitle(typeName, item),
      description: this.createMetaDescription(typeName, item),
      url: pageUrl,
      mainEntity: {
        '@type': 'ItemList',
        name: `Relaterade sökningar för ${item ? item.name : typeName}`,
        description: this.createMetaDescription(typeName, item),
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Hem',
            item: this.appConfigService.appConfig.baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Relaterade sökningar',
            item: `${this.appConfigService.appConfig.baseUrl}/related`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: typeName,
            item: pageUrl,
          },
        ],
      },
      publisher: {
        '@type': 'Organization',
        name: this.appConfigService.appConfig.siteName,
        url: this.appConfigService.appConfig.baseUrl,
      },
    };

    // Add item-specific structured data if available
    if (item) {
      structuredData.breadcrumb.itemListElement.push({
        '@type': 'ListItem',
        position: 4,
        name: item.name,
        item: pageUrl,
      });
    }

    if (isPlatformBrowser(this.platformId)) {
      const existingScript = this.document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      this.document.head.appendChild(script);
    }
  }
}
